/**
 * taskTypeVocabulary — the one Hebrew vocabulary for a run's own task type, the last of the three
 * classification fields an analysis carries beside `risk_level` and `complexity` to get a module.
 *
 * The scale is the closed five-arm `helpdesk | m365 | network | hardware | other`, declared three
 * times in the same producer:
 *
 *   - `_VALID_TASK_TYPES` (`backend/app/services/direct_llm_analyst.py`:111) is the set the parser
 *     validates the model's answer against;
 *   - `:214` writes the same five arms into the model's own output-format example
 *     (`"task_type": "helpdesk" | "m365" | "network" | "hardware" | "other"`), the only declaration
 *     that states an order;
 *   - `helpdesk_analyst.py`:56 carries them as the dataclass field's own comment.
 *
 * The value reaches the frontend as `AgentRun.analysis["task_type"]`, and two backend projections
 * pass it through *unchanged* rather than re-wording or re-validating it — `handoff_center.py`:255
 * bounds it to a length and `ticket_workbench.py`:226 clips it to one — so a token this map does not
 * hold is exactly as reachable on those surfaces as on the run's own.
 *
 * Three writers mint it and all three are inside the scale: the provider path validates the model's
 * answer against `_VALID_TASK_TYPES` and replaces anything else with `"helpdesk"`
 * (`direct_llm_analyst.py`:943-945), and the two deterministic fallbacks are literals —
 * `direct_llm_analyst.py`:1072 and `helpdesk_analyst.py`:398, both `"helpdesk"`. **On the pilot
 * default (no `ANALYSIS_PROVIDER`) every run therefore carries `"helpdesk"`**, so the four surfaces
 * that printed the raw token did so on the default build, not only on a provider-configured one.
 *
 * The frontend did not treat the scale as one field. Six files each kept their own copy of the same
 * five-arm map — `TASK_TYPE_HE` in `AgentRunDetailPage`:63, `ApprovalCenterPage`:49 and
 * `AgentRunPanel`:77, `TASK_TYPE_LABEL` in `HandoffBriefing`:77, an inline copy in
 * `AgentRunsDashboardPage`:435-437 and a second in the same file's stats strip at `:1239` — plus
 * `_TASK_TYPE_OPTIONS` (`AgentRunPanel`:465), which is the same map written a seventh time as the
 * edit control's option list. Every one of them fell through to the producer's own token
 * (`|| value` / `?? value`), so not one could say "this build does not know this value".
 *
 * And four surfaces printed the producer's token on the default build. Three never looked the field
 * up at all:
 *
 *   - `FeedbackPage`:621 printed the token bare in a table column headed `נושא`, in the same table
 *     where every other cell is a percentage or a Hebrew phrase;
 *   - the same page's bar chart at `:841` used it as the row's own `label` under the heading
 *     `תיקונים לפי סוג משימה`;
 *   - and its recommended-action sentence at `:195` interpolated it inside Hebrew prose —
 *     `נושא "helpdesk" — ... 30% שיעור תיקון` — which is the operator-facing evidence surface the
 *     pilot is judged on.
 *
 * The fourth did look the field up, and worded its *visible* label from the inline map above, but
 * interpolated the key rather than the label into its own tooltip (`AgentRunsDashboardPage`:1433,
 * `4 עריכות בקריאות סוג helpdesk`). That column is the same field: `/agent-runs/feedback/stats`
 * groups `edits_by_task_type` by `AgentRun.analysis["task_type"]` (`agent_runs.py`:1164), so its keys
 * are this scale's tokens and on this build they are all `"helpdesk"`. A surface that words one of
 * its two printings of a value is not a surface that words the value.
 *
 * Two further sites held the token in a branch no current build can reach, and are corrected for the
 * same reason rather than pinned: the strip chip's fallback tooltip at `:1251` (`סנן לפי: ${ttype}`)
 * is only taken when `task_type_stats` has no row for a key in `task_type_distribution`, and the
 * two group-bys behind those two maps share one `where` clause (`agent_runs.py`:802-844), so every
 * distribution key has a stats row with a positive `total` and the branch is dead on today's
 * payload. It is reachable again the moment those two queries stop agreeing, which is why the
 * resolver is used on both sides of it rather than only the live one.
 *
 * Two rules this module exists to hold, the same two `runStatusVocabulary`, `riskGradeVocabulary`
 * and `complexityVocabulary` hold:
 *
 *   1. One scale, re-derived from the producer. `taskTypeVocabulary.test.js` parses
 *      `_VALID_TASK_TYPES` out of the Python source and asserts this map's key set EQUALS it, and
 *      re-derives the same five arms two further, independent ways — the prompt's example and the
 *      dataclass comment — so a sixth arm, a renamed arm or a dropped one reddens the suite instead
 *      of reaching an operator as a bare English word.
 *   2. An unrecognised value is NAMED, never echoed — the reason `taskTypeLabel` will not put a
 *      token on screen. A value outside the scale means this bundle is behind the producer, or the
 *      row predates the validation; either way the operator is told that, not shown `helpdesk`.
 *
 * Wording. The five words are the ones all seven copies already used, so no screen's frames change;
 * what changes is that there is now one list of them. `m365` keeps the product name rather than a
 * translated word, which the suite pins — it is the one arm whose label is not Hebrew, and the
 * obvious "translation" (`אפליקציות`) is what the ticket category scale calls something else.
 *
 * WHY THERE IS NO TONE HERE, unlike `complexityVocabulary`. Five colour ternaries on `complexity`
 * all ended in `else → the lowest grade`, so that module needed a fourth arm to draw an unknown
 * value as. Nothing on `task_type` is coloured per arm: all eleven sites draw one fixed indigo or
 * blue class regardless of which arm was read, so there is no fall-through to fix and a tone would
 * be an exported arm with no call site. The class each site draws stays that site's business.
 *
 * WHAT THIS FILE CANNOT SEE, stated so the coverage claim cannot go short.
 *
 * `task_type` is also a *different* field on a different object: `ProviderUsage` carries one beside
 * `task_type="diagnosis"` (`openclaw_adapter.py`:794, a usage/cost record of the call the LLM was
 * asked to make, where the token names the call rather than the ticket). It is not this scale and is
 * not on any frontend surface, which is why the guard's writer scan is scoped to the two modules
 * that mint the *analysis* field rather than run over `backend/`.
 *
 * This field is NOT the ticket `ai_category` scale and the two must not be unified.
 * `manager_agent`'s `_TASK_TO_CATEGORY` (`:769`, read at `:837`) deliberately *translates* an
 * analysis task type into that other vocabulary — `helpdesk` becomes `Software`, which
 * `ticketCategoryVocabulary` words `תוכנה`, while this module words the same stored value
 * `הלפדסק`. So the same run reads `הלפדסק` on the run surfaces and `תוכנה` on the approved ticket,
 * and that is the mapping's decision, not drift. A future author who "fixes" the disagreement by
 * copying one map over the other breaks the taxonomy: the four shared tokens (`m365`, `network`,
 * `hardware`, `other`) are the only ones the two scales can be checked against for agreement, and
 * even there `m365` differs by design (`M365` here, `דואר` there). There is deliberately no
 * cross-module word assertion; the guard pins only that this module does not carry `תוכנה`.
 *
 * `FeedbackPage` builds two knowledge-search URLs from the same token — the recommended-action
 * link's `to`, and the per-row 📝 affordance in the task-type table — and both must keep the raw
 * value: a search keyword is a query, not a label, and wording it would search for Hebrew prose
 * instead of the analysis theme. Those are the two sites on this field where the token is correct on
 * screen, and they are recorded here so a later sweep does not "fix" them. The guard's bare-print
 * sweep exempts them by their `encodeURIComponent` call rather than by line.
 */

/** The five arms of the producer's task-type scale, by token, in the order the prompt declares them. */
export const TASK_TYPE_HE = {
  helpdesk: "הלפדסק",
  m365: "M365",
  network: "רשת",
  hardware: "חומרה",
  other: "אחר",
};

/** Shown for a value the scale does not hold. Never the token itself. */
export const TASK_TYPE_UNKNOWN_HE = "סוג משימה לא מזוהה";

/**
 * The Hebrew word for a stored task type. `hasOwnProperty` rather than `TASK_TYPE_HE[value] ||`:
 * a bare lookup answers an inherited key (`"constructor"`, `"toString"`) with a function, which a
 * `||` fallback would treat as a hit and render.
 *
 * `null`/`undefined` and a value on another scale both land on the named unknown rather than on an
 * empty string, so a caller cannot print a token it does not have.
 *
 * One consequence worth knowing before calling this on an aggregated row. The per-run surfaces call
 * it with a value the producer minted, but `FeedbackPage`'s table and bar chart call it with a
 * **group-by key**, so two distinct off-scale tokens in one response render under the same name.
 * That is the honest failure — the alternative is English identifiers in a Hebrew table — and the
 * distinct tokens remain inspectable in the API payload. No such token can be minted by a current
 * build, so it takes a row written before the validation existed to reach that arm at all.
 */
export function taskTypeLabel(value) {
  return Object.prototype.hasOwnProperty.call(TASK_TYPE_HE, value)
    ? TASK_TYPE_HE[value]
    : TASK_TYPE_UNKNOWN_HE;
}

/**
 * The scale's arms in canonical order — the prompt's own order — for a surface that has to lay
 * several of them out. Read off this module rather than written again at the call site: the edit
 * control that used to hold its own copy is the reason there were seven of these lists.
 */
export const TASK_TYPE_ORDER = Object.keys(TASK_TYPE_HE);

/**
 * The same five arms as `{ value, label }` options, for a control that has to offer them. Derived
 * from `TASK_TYPE_HE` rather than written out, so a renamed arm or a reworded label cannot reach a
 * picker and a badge as two different things.
 */
export const TASK_TYPE_OPTIONS = TASK_TYPE_ORDER.map((value) => ({ value, label: TASK_TYPE_HE[value] }));
